import z from 'zod';
import { entitySchema, paginationParamsSchema, responseBaseSchema } from '../shared.types';
import { selectedFilterItemSchema } from '../filtersDrawer/filtersDrawer.types';

// Base BaselineTask schema
export const baselineTaskSchema = z.object({
    ...entitySchema.shape,
    name: z.string().min(1, 'Name cannot be empty'),
    isActive: z.boolean(),
    isDeprecated: z.boolean().optional().default(false),
    targetDaysOut: z.number().int().optional().nullable(),
    startDaysOut: z.number().int().optional().nullable(),
    isStartDateActive: z.boolean().optional(),
    baselineId: z.number().int().optional().nullable(),
    targetStaticDate: z.string().optional().nullable(),
    startStaticDate: z.string().optional().nullable(),
    anticipatedEffort: z.string().optional().nullable(),
    ordinal: z.number().int().optional().nullable(),
    isLocationTaskDateAdjustable: z.boolean().optional().default(true),
    isMilestone: z.boolean().optional().default(false),
    isLocationTaskListActive: z.boolean().optional().default(true),
    includeCertificationCheckbox: z.boolean().optional().default(false),
    additionalInfo: z.string().optional().nullable(),
    contactName: z.string().optional().nullable(),
    contactEmail: z.string().optional().nullable(),
    // ID fields (what the form actually uses)
    jobTitleId: z.number().int().optional().default(0),
    departmentId: z.number().int().optional().default(0),
    communityId: z.number().int().optional().default(0),
    phaseId: z.number().int().optional().nullable(),
    // String fields (for display/API compatibility)
    baselineJobTitle: z.string().optional().nullable(),
    department: z.string().optional().nullable(),
    community: z.string().optional().nullable(),
    discipline: z.string().optional().nullable(),
    isBaselineTaskValidationRulesActive: z.boolean().optional().default(false),
    links: z.array(z.any()).optional().default([]),
    prereqTasks: z.array(z.any()).optional().default([]),
    steps: z.array(z.any()).optional().default([]),
    taskTagIds: z.array(z.number().int()).optional().default([]),
    sameNameCount: z.number().int().optional().default(0),
    createdByName: z.string().optional().nullable(),
    modifiedByName: z.string().optional().nullable(),
    createdDate: z.string(),
    modifiedDate: z.string().optional().nullable(),
    callWebhookOnStatusChange: z.boolean().optional().default(false),
    workflows: z.array(z.any()).optional().default([]),
    certifiedTaskId: z.number().int().optional().nullable(),
    tags: z.array(z.any()).optional().default([]),
    totalDependencies: z.number().int().optional().default(0),
    totalSubtasks: z.number().int().optional().default(0),
});

// BaselineTaskItem with isChecked for UI
export const baselineTaskItemSchema = baselineTaskSchema.extend({
    isChecked: z.boolean().optional().default(false),
});

// Multi-column sort schema
export const multiColumnSortSchema = z.object({
    sortBy: z.string(),
    sortDir: z.string(),
});

// BaselineTask search parameters
export const baselineTaskSearchSchema = z.object({
    ...paginationParamsSchema.shape,
    subscriptionId: z.string(),
    baselineId: z.string(),
    departments: z.number().array().optional().default([]),
    baselineJobTitles: z.number().array().optional().default([]),
    communities: z.number().array().optional().default([]),
    phases: z.number().array().optional().default([]),
    disciplines: z.number().array().optional().default([]),
    tags: z.number().array().optional().default([]),
    isMobile: z.boolean().optional().default(false),
    isActive: z.boolean().nullable().optional(),
    name: z.string().optional().default(''),
    additionalInfo: z.string().optional().default(''),
    multiColumnSort: z.array(multiColumnSortSchema).optional().default([]),
    createdDateStart: z.string().optional().nullable(),
    createdDateEnd: z.string().optional().nullable(),
});

// Option schema for filters
export const optionSchema = z.object({
    id: z.number().int(),
    name: z.string(),
});

// BaselineTask list filters response
export const baselineTaskListFiltersResponseSchema = z.object({
    departments: z.array(optionSchema),
    baselineJobTitles: z.array(optionSchema),
    communities: z.array(optionSchema),
    phases: z.array(optionSchema),
    disciplines: z.array(optionSchema),
    tags: z.array(optionSchema),
    isStartDateActive: z.boolean(),
    isTaskStepsActive: z.boolean(),
    isTaskEffortActive: z.boolean(),
    isTaskFeedbackActive: z.boolean(),
    isTaskDependenciesActive: z.boolean(),
    isProjectWidgetActive: z.boolean(),
    isWebhooksActive: z.boolean(),
    isBaselineTaskValidationRulesActive: z.boolean(),
});

// BaselineTask form filters response
export const baselineTaskFormFiltersResponseSchema = z.object({
    departments: z.array(optionSchema),
    baselineJobTitles: z.array(optionSchema),
    communities: z.array(optionSchema),
    phases: z.array(optionSchema),
    taskTags: z.array(optionSchema),
    certifiedTaskId: z.number().int().nullable(),
    subscriptionFiles: z.array(z.any()),
    workflows: z.array(z.any()).optional(),
});

// BaselineTask search response
export const baselineTaskSearchResponseSchema = z.object({
    results: z.array(baselineTaskItemSchema),
    totalCount: z.number().int(),
    // Absent on environments that predate saved columns; a missing value must not fail the whole parse.
    columnOrder: z.array(z.string()).default([]),
});

// Request schemas
export const baselineTaskListFiltersRequestSchema = z.object({
    subscriptionId: z.string(),
    baselineId: z.string(),
});

export const baselineTaskFormFiltersRequestSchema = z.object({
    subscriptionId: z.string(),
    baselineId: z.string(),
});

// ---- Gantt search ----

export const baselineTaskGanttSearchSchema = z.object({
    baselineId: z.string(),
    openingDate: z.string(),
    name: z.string().optional().default(''),
    additionalInfo: z.string().optional().default(''),
    isActive: z.boolean().nullable().optional(),
    departments: z.number().array().optional().default([]),
    baselineJobTitles: z.number().array().optional().default([]),
    communities: z.number().array().optional().default([]),
    phases: z.number().array().optional().default([]),
    disciplines: z.number().array().optional().default([]),
    // No tags filter: SearchGantt.Query / GanttSearchQuery on the API have no Tags property, so
    // sending one would be silently ignored. Add here once the endpoint supports it.
    createdDateStart: z.string().optional().nullable(),
    createdDateEnd: z.string().optional().nullable(),
});

// Gantt task shape returned by baselinetask/search-gantt (distinct from the list-search BaselineTask)
export const ganttTaskSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    isActive: z.boolean().optional().default(true),
    isMilestone: z.boolean().optional().default(false),
    contingentId: z.number().int().nullable().optional(),
    includeCertificationCheckbox: z.boolean().optional().default(false),
    startDate: z.string().nullable().optional(),
    targetDate: z.string(),
    targetDaysOut: z.number().int().nullable().optional(),
    targetStaticDate: z.string().nullable().optional(),
    startDaysOut: z.number().int().nullable().optional(),
    startStaticDate: z.string().nullable().optional(),
});

// Drop individual malformed rows instead of failing the whole array: a single task missing
// name/targetDate would otherwise blank the entire chart. droppedTaskCount lets the view tell the
// user the chart is incomplete, since a dropped row also removes any dependency link drawn to it.
export const ganttSearchResponseSchema = z
    .object({
        ...responseBaseSchema.shape,
        tasks: z.array(z.unknown()),
    })
    .transform((response) => {
        const tasks: z.infer<typeof ganttTaskSchema>[] = [];
        response.tasks.forEach((row) => {
            const parsed = ganttTaskSchema.safeParse(row);
            if (parsed.success) {
                tasks.push(parsed.data);
            } else {
                console.warn('ganttSearchResponse: dropping malformed task row', parsed.error);
            }
        });
        return { ...response, tasks, droppedTaskCount: response.tasks.length - tasks.length };
    });

// ---- Update Task Dates ----

export const updateTaskDatesRequestSchema = z.object({
    taskId: z.number().int(),
    startDate: z.string().nullable().optional(),
    targetDate: z.string(),
    fakeOpeningDate: z.string(),
});

export const updateTaskDatesResponseSchema = responseBaseSchema;

// Type exports
export type BaselineTask = z.infer<typeof baselineTaskSchema>;
export type BaselineTaskItem = z.infer<typeof baselineTaskItemSchema>;
export type MultiColumnSort = z.infer<typeof multiColumnSortSchema>;
export type BaselineTaskSearch = z.infer<typeof baselineTaskSearchSchema>;
export type Option = z.infer<typeof optionSchema>;
export type BaselineTaskListFiltersResponse = z.infer<typeof baselineTaskListFiltersResponseSchema>;
export type BaselineTaskFormFiltersResponse = z.infer<typeof baselineTaskFormFiltersResponseSchema>;
export type BaselineTaskSearchResponse = z.infer<typeof baselineTaskSearchResponseSchema>;
export type BaselineTaskListFiltersRequest = z.infer<typeof baselineTaskListFiltersRequestSchema>;
export type BaselineTaskFormFiltersRequest = z.infer<typeof baselineTaskFormFiltersRequestSchema>;
export type BaselineTaskGanttSearch = z.infer<typeof baselineTaskGanttSearchSchema>;
export type GanttTask = z.infer<typeof ganttTaskSchema>;
export type GanttSearchResponse = z.infer<typeof ganttSearchResponseSchema>;
export type UpdateTaskDatesRequest = z.infer<typeof updateTaskDatesRequestSchema>;
export type UpdateTaskDatesResponse = z.infer<typeof updateTaskDatesResponseSchema>;